import { apiGet, apiPost, apiPut, apiDelete } from './client';
import { Issuer, ApiResponse, PaginatedResponse } from '@tinyrewards/shared';

// ── Issuer CRUD ──────────────────────────────────────────────────────────────

export function getIssuers(): Promise<ApiResponse<Issuer[]>> {
    return apiGet<Issuer[]>('/api/issuers');
}

export function createIssuer(data: { issuerName: string; issuerURL?: string; issuerEmail?: string; issuerDescription?: string }): Promise<ApiResponse<Issuer>> {
    return apiPost<Issuer>('/api/issuers', data);
}

export function updateIssuer(id: string, data: Partial<{ issuerName: string; issuerURL: string; issuerEmail: string; issuerDescription: string }>): Promise<ApiResponse<Issuer>> {
    return apiPut<Issuer>(`/api/issuers/${id}`, data);
}

export function deleteIssuer(id: string): Promise<ApiResponse<void>> {
    return apiDelete<void>(`/api/issuers/${id}`);
}

// ── Stats ────────────────────────────────────────────────────────────────────

export interface IssuerStats {
    badgesCreated: number;
    badgesIssued: number;
}

export function getIssuerStats(id: string): Promise<ApiResponse<IssuerStats>> {
    return apiGet<IssuerStats>(`/api/issuers/${id}/stats`);
}

// ── Team ─────────────────────────────────────────────────────────────────────

export interface TeamMember {
    UserID: string;
    Email: string;
    FirstName: string | null;
    LastName: string | null;
    Status: string;
}

export function getIssuerTeam(id: string, page = 1): Promise<PaginatedResponse<TeamMember>> {
    return apiGet<TeamMember[]>(`/api/issuers/${id}/team?page=${page}`) as Promise<PaginatedResponse<TeamMember>>;
}

export function inviteToTeam(id: string, email: string): Promise<ApiResponse<void>> {
    return apiPost<void>(`/api/issuers/${id}/team/invite`, { email });
}

export function removeFromTeam(id: string, userId: string): Promise<ApiResponse<void>> {
    return apiDelete<void>(`/api/issuers/${id}/team/${userId}`);
}

// ── Pending team invites for the current user (Notifications tab) ────────────

export interface PendingTeamInviteRow {
    IssuerID: string;
    IssuerName: string;
    IssuerType: string | null;
    HostDomain: string | null;
    ImageFileName: string | null;
    isVerified: boolean;
    CreatedDate: string;
}

export type PendingListResponse<T> = ApiResponse<T[]> & {
    total: number;
    page: number;
    pageSize: number;
};

export function listPendingTeamInvites(page = 1): Promise<PendingListResponse<PendingTeamInviteRow>> {
    return apiGet<PendingTeamInviteRow[]>(`/api/issuers/team-invites/pending?page=${page}`) as Promise<
        PendingListResponse<PendingTeamInviteRow>
    >;
}

export function acceptTeamInvite(issuerID: string): Promise<ApiResponse<void>> {
    return apiPost<void>(`/api/issuers/${issuerID}/team/accept`, {});
}

export function declineTeamInvite(issuerID: string): Promise<ApiResponse<void>> {
    return apiPost<void>(`/api/issuers/${issuerID}/team/decline`, {});
}

// ── Pending issuer-sharing requests for issuers I admin ──────────────────────

export interface PendingIssuerShareRow {
    ID: string;
    IssuerID_From: string;
    IssuerName_From: string;
    IssuerDescription_From: string | null;
    IssuerDomain_From: string | null;
    IssuerType_From: string | null;
    OrganizationType_From: string | null;
    IssuerImageFileName_From: string | null;
    IssuerID_To: string;
    IssuerName_To: string;
    IssuerDescription_To: string | null;
    IssuerDomain_To: string | null;
    LastUpdatedDate: string;
}

interface SharingRequestEnvelope<T> {
    pendingRequests: T[];
    totalRequests: number;
    pageSize: number;
}

const SHARING_PAGE_SIZE = 25;

async function fetchSharingList<T>(url: string, page: number): Promise<PendingListResponse<T>> {
    const res = (await apiGet<SharingRequestEnvelope<T>>(url)) as ApiResponse<SharingRequestEnvelope<T>>;
    if (!res.success || !res.data) {
        return { ...res, data: [], total: 0, page, pageSize: SHARING_PAGE_SIZE } as PendingListResponse<T>;
    }
    return {
        success: true,
        data: res.data.pendingRequests,
        total: res.data.totalRequests,
        page,
        pageSize: res.data.pageSize ?? SHARING_PAGE_SIZE,
    };
}

export function listPendingIssuerShareRequests(page = 1): Promise<PendingListResponse<PendingIssuerShareRow>> {
    return fetchSharingList<PendingIssuerShareRow>(`/api/issuer-sharing/requests?page=${page}`, page);
}

export function approveIssuerShareRequest(
    id: string,
    payload: { acceptAllBadges: boolean },
): Promise<ApiResponse<void>> {
    return apiPost<void>(`/api/issuer-sharing/${id}/approve`, payload);
}

export function declineIssuerShareRequest(
    id: string,
    payload: { message: string; blockFutureRequest?: boolean },
): Promise<ApiResponse<void>> {
    return apiPost<void>(`/api/issuer-sharing/${id}/decline`, payload);
}

// ── Pending badge-sharing requests for issuers I admin ───────────────────────

export interface PendingBadgeShareRow {
    ID: string;
    OpenBadgeID: string;
    IssuerName_From: string;
    IssuerDomain_From: string | null;
    IssuerType_From: string | null;
    OrganizationType_From: string | null;
    IssuerID_To: string;
    IssuerName_To: string;
    IssuerType_To: string | null;
    status: string | null;
    LastUpdatedDate: string;
    BadgeName: string;
    BadgeDescription: string | null;
    Image: string | null;
}

export function listPendingBadgeShareRequests(page = 1): Promise<PendingListResponse<PendingBadgeShareRow>> {
    return fetchSharingList<PendingBadgeShareRow>(`/api/issuer-sharing/badge-requests?page=${page}`, page);
}

export function approveBadgeShareRequest(id: string): Promise<ApiResponse<void>> {
    return apiPost<void>(`/api/issuer-sharing/badge-requests/${id}/approve`, {});
}

export function declineBadgeShareRequest(id: string, message: string): Promise<ApiResponse<void>> {
    return apiPost<void>(`/api/issuer-sharing/badge-requests/${id}/decline`, { message });
}

// ── Sharing ───────────────────────────────────────────────────────────────────

export interface SharingPartner {
    ID: string;
    IssuerID: string;
    IssuerName: string;
    IssuerDomain: string | null;
    status: string | null;
}

export interface IssuerPartners {
    issuersSharedTo: SharingPartner[];
    issuersSharedFrom: SharingPartner[];
}

export function getIssuerPartners(id: string): Promise<ApiResponse<IssuerPartners>> {
    return apiGet<IssuerPartners>(`/api/issuer-sharing/partners/${id}`);
}

export function requestSharing(fromIssuerID: string, toIssuerID: string): Promise<ApiResponse<void>> {
    return apiPost<void>('/api/issuer-sharing/request', { from_IssuerID: fromIssuerID, to_IssuerID: toIssuerID });
}

export function breakSharing(sharingId: string): Promise<ApiResponse<void>> {
    return apiDelete<void>(`/api/issuer-sharing/${sharingId}`);
}

// ── All issuers (public) for sharing target picker ────────────────────────────

export interface PublicIssuer {
    IssuerID: string;
    Name: string;
    HostDomain: string;
}

export function getAllIssuers(): Promise<ApiResponse<PublicIssuer[]>> {
    return apiGet<PublicIssuer[]>('/api/issuers/all');
}

// ── API Keys ─────────────────────────────────────────────────────────────────

export interface ApiKeyInfo {
    primaryKey: string;
    secondaryKey: string;
    issuerUniqueURLCode: string;
    whiteListedOrigins: { ID: string; URL: string }[];
}

export function getApiKeys(id: string): Promise<ApiResponse<ApiKeyInfo>> {
    return apiGet<ApiKeyInfo>(`/api/issuers/${id}/api-keys`);
}

export function rotateApiKey(id: string, keyType: 'primary' | 'secondary'): Promise<ApiResponse<{ token: string; keyType: string }>> {
    return apiPost<{ token: string; keyType: string }>(`/api/issuers/${id}/api-keys/rotate`, { keyType });
}

export function addApiOrigin(id: string, url: string): Promise<ApiResponse<{ id: string; url: string }>> {
    return apiPost<{ id: string; url: string }>(`/api/issuers/${id}/api-origins`, { url });
}

export function removeApiOrigin(id: string, originId: string): Promise<ApiResponse<void>> {
    return apiDelete<void>(`/api/issuers/${id}/api-origins/${originId}`);
}

// ── Webhooks ──────────────────────────────────────────────────────────────────

export interface IssuerWebhook {
    ID: string;
    URL: string;
    IsActive: boolean;
}

export function getWebhooks(id: string): Promise<ApiResponse<IssuerWebhook[]>> {
    return apiGet<IssuerWebhook[]>(`/api/issuers/${id}/webhooks`);
}

export function addWebhook(id: string, url: string): Promise<ApiResponse<void>> {
    return apiPost<void>(`/api/issuers/${id}/webhooks`, { url });
}

export function removeWebhook(id: string, webhookId: string): Promise<ApiResponse<void>> {
    return apiDelete<void>(`/api/issuers/${id}/webhooks/${webhookId}`);
}
